import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  ClipboardList,
  DollarSign,
  ArrowRight,
  Home,
  Wrench,
  BedDouble,
  Bath,
  Square,
  MessageCircle,
  Phone,
  TrendingUp,
  Calendar,
  AlertCircle,
} from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { useLanguage } from '../contexts/LanguageContext';
import Logo from '../components/Logo';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend);

const DEMO_SAMPLE_DATA = {
  properties: [
    { id: '1', name: 'Sunset Apartments - Unit 101', address: '123 Main St, Apt 101', type: 'residential', status: 'occupied', monthlyRent: 1850, bedrooms: 2, bathrooms: 2 },
    { id: '2', name: 'Riverside Condo', address: '456 Oak Ave', type: 'residential', status: 'occupied', monthlyRent: 2200, bedrooms: 3, bathrooms: 2 },
    { id: '3', name: 'Downtown Office Suite', address: '789 Business Blvd', type: 'commercial', status: 'vacant', monthlyRent: 3500, bedrooms: 0, bathrooms: 2 },
  ],
  tenants: [
    { id: '1', tenantName: 'Sarah Johnson', tenantEmail: 'sarah.j@email.com', tenantPhone: '+1 (555) 234-5678', propertyName: 'Sunset Apartments - Unit 101', status: 'active', monthlyRent: 1850, leaseEnd: '2025-08-31' },
    { id: '2', tenantName: 'Mike Chen', tenantEmail: 'mike.chen@email.com', tenantPhone: '+1 (555) 876-5432', propertyName: 'Riverside Condo', status: 'active', monthlyRent: 2200, leaseEnd: '2025-11-15' },
  ],
  tickets: [
    { id: '1', title: 'Leaking kitchen faucet', propertyName: 'Sunset Apartments - Unit 101', status: 'in-progress', priority: 'medium', createdAt: '2025-01-28' },
    { id: '2', title: 'HVAC not cooling', propertyName: 'Riverside Condo', status: 'open', priority: 'high', createdAt: '2025-02-01' },
    { id: '3', title: 'Broken bedroom blind', propertyName: 'Sunset Apartments - Unit 101', status: 'completed', priority: 'low', createdAt: '2025-01-20' },
  ],
  totalExpenses: 1247.50,
  expenseByCategory: [
    { category: 'Repairs', amount: 485 },
    { category: 'Maintenance', amount: 312.50 },
    { category: 'Utilities', amount: 280 },
    { category: 'Insurance', amount: 170 },
  ],
  expenseByProperty: [
    { name: 'Sunset Apartments - Unit 101', amount: 620 },
    { name: 'Riverside Condo', amount: 427.50 },
    { name: 'Downtown Office Suite', amount: 200 },
  ],
  monthlyTrend: [
    { month: 'Oct', income: 4050, expenses: 980 },
    { month: 'Nov', income: 4050, expenses: 1120 },
    { month: 'Dec', income: 4050, expenses: 1050 },
    { month: 'Jan', income: 4050, expenses: 1247.50 },
  ],
};

const DEMO_NAV = [
  { id: 'dashboard', labelKey: 'sidebar.dashboard', Icon: LayoutDashboard },
  { id: 'properties', labelKey: 'sidebar.properties', Icon: Building2 },
  { id: 'tenants', labelKey: 'sidebar.tenants', Icon: Users },
  { id: 'tickets', labelKey: 'sidebar.tickets', Icon: ClipboardList },
  { id: 'expenses', labelKey: 'sidebar.expenses', Icon: DollarSign },
];

const DemoSidebar = ({ currentView, onNavigate, t }) => (
  <div className="w-72 bg-white fixed h-screen border-r border-slate-200 flex flex-col shadow-sm">
    <div className="p-5 flex-1">
      <div className="flex items-center gap-3 mb-6">
        <Logo type="icon" width="44px" height="44px" className="shrink-0" />
        <div>
          <h1 className="text-base font-semibold text-slate-800">EasyProperty</h1>
          <p className="text-xs text-slate-500">Interactive demo</p>
        </div>
      </div>
      <nav className="space-y-1">
        {DEMO_NAV.map((item) => {
          const Icon = item.Icon;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm font-medium transition-colors ${
                currentView === item.id
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" strokeWidth={1.75} />
              <span>{t(item.labelKey)}</span>
            </button>
          );
        })}
      </nav>
    </div>
    <div className="p-5 border-t border-slate-200 space-y-2">
      <Link
        to="/register"
        className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
      >
        Get started free
        <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
      </Link>
      <Link
        to="/"
        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg transition-colors"
      >
        <Home className="w-4 h-4" strokeWidth={1.75} />
        Back to home
      </Link>
    </div>
  </div>
);

const DemoPage = () => {
  const { t } = useLanguage();
  const [currentView, setCurrentView] = useState('dashboard');
  const { properties, tenants, tickets, totalExpenses, expenseByCategory, expenseByProperty, monthlyTrend } = DEMO_SAMPLE_DATA;

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'in-progress': return 'bg-slate-100 text-slate-800 border-slate-300';
      case 'completed': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getPropertyStatusColor = (status) => {
    switch (status) {
      case 'occupied': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'vacant': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const DemoBanner = () => (
    <div className="bg-slate-800 text-white py-2.5 px-4 text-center text-sm">
      <span className="inline-flex items-center gap-2">
        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
        Sample data only. <Link to="/register" className="font-medium underline hover:no-underline text-white">Sign up free</Link> to manage your own properties.
      </span>
    </div>
  );

  const dashboardTrendData = {
      labels: monthlyTrend.map((d) => d.month),
      datasets: [
        {
          label: 'Income ($)',
          data: monthlyTrend.map((d) => d.income),
          borderColor: 'rgb(30, 64, 175)',
          backgroundColor: 'rgba(30, 64, 175, 0.08)',
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Expenses ($)',
          data: monthlyTrend.map((d) => d.expenses),
          borderColor: 'rgb(148, 163, 184)',
          backgroundColor: 'rgba(148, 163, 184, 0.08)',
          fill: true,
          tension: 0.3,
        },
      ],
    };
    const dashboardChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' },
      },
      scales: {
        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } },
        x: { grid: { display: false } },
      },
    };

  const DashboardView = () => (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/15 flex items-center justify-center text-xl font-semibold text-white border border-white/20">D</div>
            <div>
              <p className="text-slate-300 text-sm font-medium">Welcome back</p>
              <p className="text-xl font-semibold text-white tracking-tight">Demo User</p>
              <p className="text-slate-400 text-xs mt-0.5">Portfolio overview · Last 30 days</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: t('dashboard.owner.stats.properties'), value: properties.length, Icon: Building2, color: 'bg-slate-100 text-slate-700' },
          { label: t('dashboard.owner.stats.tenants'), value: tenants.length, Icon: Users, color: 'bg-emerald-50 text-emerald-700' },
          { label: t('dashboard.owner.stats.tickets'), value: tickets.length, Icon: ClipboardList, color: 'bg-amber-50 text-amber-700' },
          { label: t('dashboard.owner.stats.expenses'), value: `$${totalExpenses.toFixed(2)}`, Icon: DollarSign, color: 'bg-slate-100 text-slate-700' },
        ].map(({ label, value, Icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-semibold text-slate-800 tabular-nums">{value}</p>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-0.5">{label}</p>
              </div>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
                <Icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
          <h2 className="text-sm font-semibold text-slate-800">Income vs expenses</h2>
          <span className="text-xs text-slate-500 ml-1">(sample trend)</span>
        </div>
        <div className="p-4 h-64">
          <Line data={dashboardTrendData} options={dashboardChartOptions} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
            {t('dashboard.owner.sections.properties.title')}
          </h2>
          <p className="text-xs text-slate-500 mb-4">{t('dashboard.owner.sections.properties.subtitle')}</p>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {properties.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                  <p className="text-xs text-slate-500 truncate">{p.address}</p>
                </div>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-md border shrink-0 ${getPropertyStatusColor(p.status)}`}>{p.status}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
            {t('dashboard.owner.sections.tenants.title')}
          </h2>
          <p className="text-xs text-slate-500 mb-4">{t('dashboard.owner.sections.tenants.subtitle')}</p>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {tenants.map((tn) => (
              <div key={tn.id} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{tn.tenantName}</p>
                  <p className="text-xs text-slate-500 truncate">{tn.tenantEmail}</p>
                </div>
                <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-slate-100 text-slate-700 border border-slate-200 shrink-0">{tn.status}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
            {t('dashboard.owner.sections.maintenance.title')}
          </h2>
          <p className="text-xs text-slate-500 mb-4">{t('dashboard.owner.sections.maintenance.subtitle')}</p>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {tickets.map((tk) => (
              <div key={tk.id} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{tk.title}</p>
                  <p className="text-xs text-slate-500 truncate">{tk.propertyName}</p>
                </div>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-md border shrink-0 ${getStatusColor(tk.status)}`}>{tk.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const PropertiesView = () => (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
          Properties
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your property portfolio</p>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {properties.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-slate-600" strokeWidth={1.75} />
              </div>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-lg border uppercase tracking-wide ${getPropertyStatusColor(p.status)}`}>{p.status}</span>
            </div>
            <h3 className="font-semibold text-slate-800 mb-1 text-base">{p.name}</h3>
            <p className="text-sm text-slate-500 mb-4 flex items-center gap-1.5">
              <span className="truncate">{p.address}</span>
            </p>
            <div className="flex items-center gap-4 text-xs text-slate-600 mb-4 pb-4 border-b border-slate-100">
              {p.bedrooms > 0 && <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4 text-slate-400" /> {p.bedrooms} bed</span>}
              <span className="flex items-center gap-1.5"><Bath className="w-4 h-4 text-slate-400" /> {p.bathrooms} bath</span>
              <span className="flex items-center gap-1.5"><Square className="w-4 h-4 text-slate-400" /> {p.type}</span>
            </div>
            <p className="text-lg font-semibold text-slate-800 tabular-nums">${p.monthlyRent}<span className="text-sm font-medium text-slate-500">/mo</span></p>
          </div>
        ))}
      </div>
      <p className="text-center text-sm text-slate-500 mt-6">In your account you can add, edit, and manage properties.</p>
    </div>
  );

  const TenantsView = () => (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
          Tenants
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage tenant relationships and contact info</p>
      </header>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tenant</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Property</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Rent</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Lease end</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-5 py-3.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tenants.map((tn) => (
              <tr key={tn.id} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold text-sm">
                      {tn.tenantName.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">{tn.tenantName}</p>
                      <p className="text-xs text-slate-500">{tn.tenantEmail}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={1.75} />
                    <span className="font-mono text-xs">{tn.tenantPhone || '—'}</span>
                  </div>
                </td>
                <td className="px-5 py-4 text-sm text-slate-600 max-w-[180px] truncate" title={tn.propertyName}>{tn.propertyName}</td>
                <td className="px-5 py-4 text-sm font-semibold text-slate-800 tabular-nums">${tn.monthlyRent}/mo</td>
                <td className="px-5 py-4">
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.75} />
                    {tn.leaseEnd ? new Date(tn.leaseEnd).toLocaleDateString() : '—'}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">{tn.status}</span>
                </td>
                <td className="px-5 py-4 text-right">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors border border-slate-200"
                    title="Message tenant"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
                    Message
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-center text-sm text-slate-500 mt-6">Create tenant accounts, assign properties, and message tenants from your dashboard.</p>
    </div>
  );

  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-50 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'low': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const TicketsView = () => (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
          Maintenance Requests
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Track and manage repair requests</p>
      </header>
      <div className="space-y-4">
        {tickets.map((tk) => (
          <div key={tk.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center shrink-0">
                <Wrench className="w-6 h-6 text-slate-600" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 text-sm">{tk.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.75} />
                  {tk.propertyName}
                </p>
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Opened {tk.createdAt}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-lg border capitalize ${getPriorityStyle(tk.priority)} flex items-center gap-1`}>
                {tk.priority === 'high' && <AlertCircle className="w-3.5 h-3.5" strokeWidth={2} />}
                {tk.priority} priority
              </span>
              <span className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${getStatusColor(tk.status)}`}>{tk.status}</span>
            </div>
          </div>
        ))}
      </div>
      <p className="text-center text-sm text-slate-500 mt-6">Assign requests to verified service providers and track completion.</p>
    </div>
  );

  const expenseBarData = {
      labels: expenseByCategory.map((d) => d.category),
      datasets: [{
        label: 'Amount ($)',
        data: expenseByCategory.map((d) => d.amount),
        backgroundColor: ['rgba(30, 64, 175, 0.7)', 'rgba(148, 163, 184, 0.7)', 'rgba(34, 197, 94, 0.6)', 'rgba(245, 158, 11, 0.6)'],
        borderColor: ['rgb(30, 64, 175)', 'rgb(148, 163, 184)', 'rgb(34, 197, 94)', 'rgb(245, 158, 11)'],
        borderWidth: 1,
      }],
    };
    const expenseDoughnutData = {
      labels: expenseByProperty.map((d) => d.name.length > 20 ? d.name.slice(0, 18) + '…' : d.name),
      datasets: [{
        data: expenseByProperty.map((d) => d.amount),
        backgroundColor: ['rgba(30, 64, 175, 0.8)', 'rgba(100, 116, 139, 0.8)', 'rgba(148, 163, 184, 0.8)'],
        borderColor: ['rgb(30, 64, 175)', 'rgb(100, 116, 139)', 'rgb(148, 163, 184)'],
        borderWidth: 2,
      }],
    };
    const expenseChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' } },
      scales: { y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' } } },
    };

  const ExpensesView = () => (
    <div className="p-6 space-y-6">
      <header className="mb-2">
        <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
          Expenses
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Track costs and financial performance</p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total this month</p>
              <p className="text-2xl font-semibold text-slate-800 tabular-nums mt-1">${totalExpenses.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-slate-600" strokeWidth={1.75} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">By category</p>
          <p className="text-sm text-slate-600 mt-1">Repairs, maintenance, utilities, insurance</p>
          <p className="text-xs text-slate-400 mt-2">See chart below</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">By property</p>
          <p className="text-sm text-slate-600 mt-1">Breakdown per property</p>
          <p className="text-xs text-slate-400 mt-2">See chart below</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
            <h2 className="text-sm font-semibold text-slate-800">Expenses by category</h2>
          </div>
          <div className="p-4 h-72">
            <Bar data={expenseBarData} options={expenseChartOptions} />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
            <h2 className="text-sm font-semibold text-slate-800">By property</h2>
          </div>
          <div className="p-4 h-72 flex items-center justify-center">
            <Doughnut
              data={expenseDoughnutData}
              options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Category breakdown (sample)</h3>
        <div className="space-y-2">
          {expenseByCategory.map((row) => (
            <div key={row.category} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <span className="text-sm text-slate-700">{row.category}</span>
              <span className="text-sm font-semibold text-slate-800 tabular-nums">${row.amount.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-center text-sm text-slate-500">Link expenses to maintenance tickets and export reports in your account.</p>
    </div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'properties': return <PropertiesView />;
      case 'tenants': return <TenantsView />;
      case 'tickets': return <TicketsView />;
      case 'expenses': return <ExpensesView />;
      default: return <DashboardView />;
    }
  };

  const pageTitles = {
    dashboard: t('dashboard.owner.title'),
    properties: 'Properties',
    tenants: 'Tenants',
    tickets: 'Maintenance Requests',
    expenses: 'Expenses',
  };
  const pageSubtitles = {
    dashboard: 'Overview of your portfolio',
    properties: 'Sample data — sign up to manage your own',
    tenants: 'Sample data — sign up to manage your own',
    tickets: 'Sample data — sign up to manage your own',
    expenses: 'Sample data — sign up to manage your own',
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <DemoBanner />
      <div className="flex">
        <DemoSidebar currentView={currentView} onNavigate={setCurrentView} t={t} />
        <div className="flex-1 ml-72">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10 px-6 py-4">
            <h1 className="text-lg font-semibold text-slate-800">{pageTitles[currentView]}</h1>
            <p className="text-xs text-slate-500 mt-0.5">{pageSubtitles[currentView]}</p>
          </header>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
